import React, { useEffect, useState, useRef } from "react";
import { X, Loader2, CheckCircle2, Wallet, Sparkles, Award, Phone, User, MapPin } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

// Shown exactly once per user (persisted in localStorage), after 20 minutes of
// cumulative browsing since their first visit (survives page navigation).
const SHOWN_KEY = "kiki_enquiry_shown_v2";
const FIRST_VISIT_KEY = "kiki_first_visit_ts";
const DELAY_MS = 20 * 60 * 1000; // 20 minutes

const ls = {
  get(k) {
    try { return localStorage.getItem(k); } catch { return null; }
  },
  set(k, v) {
    try { localStorage.setItem(k, v); } catch { /* noop */ }
  },
};

export default function EnquiryPopup() {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone: "", location: "" });
  const timerRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    // Never show on admin routes
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return;
    // Only once per user
    if (ls.get(SHOWN_KEY) === "1") return;

    // Establish (or read) the first-visit timestamp so the 20-min countdown
    // is cumulative across page navigations and sessions.
    let first = Number(ls.get(FIRST_VISIT_KEY));
    if (!first || Number.isNaN(first)) {
      first = Date.now();
      ls.set(FIRST_VISIT_KEY, String(first));
    }

    const remaining = Math.max(0, DELAY_MS - (Date.now() - first));

    const trigger = () => {
      if (ls.get(SHOWN_KEY) === "1") return;
      setOpen(true);
      ls.set(SHOWN_KEY, "1");
    };

    timerRef.current = setTimeout(trigger, remaining);
    return () => clearTimeout(timerRef.current);
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const close = () => {
    setOpen(false);
    setTimeout(() => setDone(false), 400);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim() || !form.location.trim()) return;
    setBusy(true);
    try {
      await api.post("/admissions", {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        city: form.location.trim(),
        course_interest: "General enquiry (popup)",
        message: `Quick enquiry via popup · Location: ${form.location.trim()}`,
      });
      setDone(true);
      toast({ title: "Enquiry received!", description: "Our counsellor will call you shortly." });
      setTimeout(close, 3000);
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: err?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 animate-fade-up"
      role="dialog"
      aria-modal="true"
      aria-labelledby="enquiry-title"
      data-testid="enquiry-popup"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#04102e]/70 backdrop-blur-sm" onClick={close} />

      {/* Card */}
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden grid md:grid-cols-2 max-h-[92vh] overflow-y-auto">
        {/* Close */}
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/90 hover:bg-slate-100 text-slate-700 shadow flex items-center justify-center transition"
          aria-label="Close"
          data-testid="enquiry-popup-close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Left promo panel — hidden on very small screens to keep it clean */}
        <div className="relative hidden md:flex bg-[#0B2A6B] text-white p-8 flex-col justify-between overflow-hidden">
          <div className="absolute -top-16 -left-16 w-56 h-56 bg-yellow-400/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-16 -right-8 w-56 h-56 bg-blue-400/20 rounded-full blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400 text-[#0B2A6B] text-[10px] font-black uppercase tracking-widest">
              <Sparkles className="h-3 w-3" /> Admissions Open • 2026
            </div>
            <h2 id="enquiry-title" className="text-3xl font-black leading-tight mt-4">
              Still exploring? <span className="text-yellow-400">Let us help you</span> choose.
            </h2>
            <p className="text-sm text-slate-200 mt-3 leading-relaxed">
              Share a few basic details and our admissions counsellor will call you to guide you to the right course.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm">
              <li className="flex items-center gap-2"><Wallet className="h-4 w-4 text-yellow-400 shrink-0" /> Earn While You Learn programmes</li>
              <li className="flex items-center gap-2"><Award className="h-4 w-4 text-yellow-400 shrink-0" /> Dual certificate — Germany + India</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-yellow-400 shrink-0" /> 95%+ placement track record</li>
            </ul>
          </div>
          <div className="relative mt-8 pt-6 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-black">Prefer to talk?</div>
            <a href="tel:+918800288994" className="mt-2 inline-flex items-center gap-2 text-lg font-bold text-white hover:text-yellow-400 transition">
              <Phone className="h-4 w-4" /> +91 88002 88994
            </a>
          </div>
        </div>

        {/* Right — form / success */}
        <div className="p-6 sm:p-8">
          {done ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-8">
              <div className="h-16 w-16 rounded-full bg-yellow-400 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-9 w-9 text-[#0B2A6B]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0B2A6B]">Thank you!</h3>
              <p className="text-slate-600 mt-2 max-w-sm">
                Thanks {form.full_name.split(" ")[0] || "there"} — our counsellor will call you on{" "}
                <span className="font-semibold text-[#0B2A6B]">{form.phone}</span> shortly.
              </p>
              <button type="button" onClick={close} className="mt-6 px-6 py-2.5 rounded-lg btn-primary" data-testid="enquiry-popup-close-success">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="enquiry-popup-form">
              {/* Mobile-only badge */}
              <div className="md:hidden inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400 text-[#0B2A6B] text-[10px] font-black uppercase tracking-widest">
                <Sparkles className="h-3 w-3" /> Admissions Open • 2026
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-yellow-600">Free counselling</p>
                <h3 className="text-xl sm:text-2xl font-bold text-[#0B2A6B] mt-1">Request a call back</h3>
                <p className="text-sm text-slate-500 mt-1">Just your name, number and location — we'll do the rest.</p>
              </div>

              <IconField icon={User}>
                <input
                  required
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  placeholder="Your name *"
                  className="w-full bg-transparent outline-none text-slate-800 text-sm"
                  data-testid="enquiry-input-name"
                />
              </IconField>

              <IconField icon={Phone}>
                <input
                  required
                  type="tel"
                  pattern="[0-9+ ]{7,15}"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="Contact number *"
                  className="w-full bg-transparent outline-none text-slate-800 text-sm"
                  data-testid="enquiry-input-phone"
                />
              </IconField>

              <IconField icon={MapPin}>
                <input
                  required
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                  placeholder="Your location / city *"
                  className="w-full bg-transparent outline-none text-slate-800 text-sm"
                  data-testid="enquiry-input-location"
                />
              </IconField>

              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg btn-primary text-base disabled:opacity-70"
                data-testid="enquiry-submit-btn"
              >
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>) : (<>Request a Call Back</>)}
              </button>

              <p className="text-[11px] text-slate-400 text-center">
                By submitting you agree to be contacted about admissions. We never share your data.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function IconField({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 h-12 px-3 rounded-lg border border-slate-200 bg-slate-50 focus-within:border-yellow-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-yellow-100 transition">
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
      {children}
    </div>
  );
}
